// Bulk Status Update Modal Component - อัพเดตสถานะรายการเช่าหลายรายการพร้อมกัน
import React, { useState } from 'react';
import Modal from '../ui/Modal';
import {
  RiCheckboxMultipleLine,
  RiArrowRightLine,
  RiAlertLine,
  RiCalendarLine,
  RiFileTextLine,
  RiStackLine
} from 'react-icons/ri';
import { getLocalDateString } from '../../utils/dateUtils';
import { cn } from '../../utils/cn';
import { getRentalStatusDisplay } from '../../utils/rentalStatusLabel';

export interface BulkStatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRentals: Array<{
    id: number;
    code: string;
    customerName: string;
    currentStatus: string;
  }>;
  onUpdate: (newStatus: string, reason: string, effectiveDate: string) => Promise<void>;
  loading?: boolean;
}

// BUG-S813-001 FIX: values MUST match the backend RentalStatus enum (UPPERCASE).
// Previously lowercase ('active', …) → endpoint RentalStatus(new_status) raised
// ValueError → every bulk status update failed with 400 "Invalid status".
// SUSPENDED/RENEWED removed — the backend rental state machine has no such
// statuses (DRAFT/ACTIVE/PENDING_CANCELLATION/COMPLETED/CANCELLED only), so
// offering them guaranteed a 400 regardless of casing.
export enum RentalStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED'
}

const BulkStatusUpdateModal: React.FC<BulkStatusUpdateModalProps> = ({
  isOpen,
  onClose,
  selectedRentals,
  onUpdate,
  loading = false
}) => {
  const [newStatus, setNewStatus] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>(
    getLocalDateString()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // D4 (MANUAL_AUDIT_SYSTEM_FINDINGS): ป้ายสถานะต้องมาจาก single source of truth
  // (utils/rentalStatusLabel.ts) ห้าม hardcode ที่นี่ — เดิมเขียนเองจนหลุดเป็น "สิ้นสุดสัญญา"
  // ทั้งที่ทั้งระบบเรียก COMPLETED ว่า "เสร็จสิ้น" (และ "สิ้นสุดสัญญา" เป็นชื่อของ
  // ReturnSourceType.CONTRACT_END = คนละเรื่อง) · แก้ที่ SoT ที่เดียวแล้วโมดัลนี้ตามทันที
  // หมายเหตุ: โมดัลเสนอ "สถานะปลายทาง" แค่ 3 ค่าโดยตั้งใจ (DRAFT/PENDING_CANCELLATION
  // ไม่ใช่ปลายทางที่ตั้งแบบ bulk ได้) แต่ "ป้าย" ของทั้ง 3 ค่ายังต้องมาจาก SoT
  const statusOptions = [
    { value: RentalStatus.ACTIVE, color: 'text-success-600' },
    { value: RentalStatus.COMPLETED, color: 'text-secondary-600' },
    { value: RentalStatus.CANCELLED, color: 'text-danger-600' }
  ].map(opt => ({ ...opt, label: getRentalStatusDisplay(opt.value).label }));

  const getStatusLabel = (status: string) => getRentalStatusDisplay(status).label || status;

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'text-secondary-600';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!newStatus) {
      newErrors.newStatus = 'กรุณาเลือกสถานะใหม่';
    }

    // ลำดับการตรวจ = ลำดับช่องบนจอ (วันที่มีผลอยู่เหนือเหตุผล) — focus ช่องแรกที่ผิด (BUG-S2230-001)
    if (!effectiveDate) {
      newErrors.effectiveDate = 'กรุณาเลือกวันที่มีผล';
    } else {
      const selectedDate = new Date(effectiveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.effectiveDate = 'วันที่มีผลต้องไม่เป็นอดีต';
      }
    }

    if (!reason.trim()) {
      newErrors.reason = 'กรุณาระบุเหตุผลในการเปลี่ยนสถานะ';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'เหตุผลต้องมีความยาวอย่างน้อย 10 ตัวอักษร';
    }

    setErrors(newErrors);

    // Scroll to first error if validation fails
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await onUpdate(newStatus, reason, effectiveDate);
      onClose();
      // รีเซ็ตฟอร์ม
      setNewStatus('');
      setReason('');
      setEffectiveDate(getLocalDateString());
      setErrors({});
    } catch (error) {
      console.error('Error updating rental statuses:', error);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setErrors({});
    }
  };

  // นับจำนวนรายการแต่ละสถานะ
  const statusCounts = selectedRentals.reduce((acc, rental) => {
    acc[rental.currentStatus] = (acc[rental.currentStatus] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="อัพเดตสถานะหลายรายการ"
      size="xl"
      footer={
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'กำลังอัพเดต...' : 'อัพเดตสถานะ'}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* แสดงรายการที่เลือก */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
            <div className="w-8 h-8 bg-gradient-to-br from-info-500 to-info-600 rounded-lg flex items-center justify-center shadow-sm">
              <RiStackLine className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-secondary-800">รายการที่เลือก ({selectedRentals.length} รายการ)</h3>
          </div>

          <div className="bg-secondary-50 rounded-lg p-4">
          {/* สรุปสถานะปัจจุบัน */}
          <div className="mb-3">
            <p className="text-xs text-secondary-600 mb-2">สถานะปัจจุบัน:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(statusCounts).map(([status, count]) => (
                <span
                  key={status}
                  className={`px-2 py-1 text-xs rounded-full bg-white ${getStatusColor(status)}`}
                >
                  {getStatusLabel(status)}: {count}
                </span>
              ))}
            </div>
          </div>

          {/* รายการแต่ละรายการ (แสดงแค่ 5 รายการแรก) */}
          <div className="space-y-1">
            {selectedRentals.slice(0, 5).map(rental => (
              <div key={rental.id} className="flex justify-between text-xs text-secondary-600">
                <span>{rental.code} - {rental.customerName}</span>
                <span className={getStatusColor(rental.currentStatus)}>
                  {getStatusLabel(rental.currentStatus)}
                </span>
              </div>
            ))}
            {selectedRentals.length > 5 && (
              <p className="text-xs text-secondary-500 text-center pt-2">
                และอีก {selectedRentals.length - 5} รายการ
              </p>
            )}
          </div>
          </div>
        </div>

        {/* เลือกสถานะใหม่ */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            สถานะใหม่ <span className="text-danger-500">*</span>
          </label>
          <select
            aria-label="สถานะใหม่"
            name="newStatus"
            value={newStatus}
            onChange={(e) => {
              setNewStatus(e.target.value);
              // Clear error when user changes value
              if (errors.newStatus) {
                setErrors(prev => ({ ...prev, newStatus: '' }));
              }
            }}
            className={cn('select', errors.newStatus && 'border-danger-300')}
            aria-invalid={Boolean(errors.newStatus)}
            aria-describedby={errors.newStatus ? 'newStatus-error' : undefined}
          >
            <option value="">เลือกสถานะใหม่</option>
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.newStatus && (
            <p id="newStatus-error" className="mt-1 text-sm text-danger-600">{errors.newStatus}</p>
          )}
        </div>

        {/* แสดงการเปลี่ยนแปลง */}
        {newStatus && (
          <div className="bg-info-50 rounded-lg p-4">
            <div className="flex items-center">
              <RiArrowRightLine className="w-5 h-5 text-info-600 mr-2" />
              <span className="text-sm font-medium text-info-900">
                การเปลี่ยนแปลง: ทุกรายการจะเปลี่ยนเป็น
                <span className={`ml-1 ${getStatusColor(newStatus)}`}>
                  {getStatusLabel(newStatus)}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* วันที่มีผล */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            <RiCalendarLine className="w-4 h-4 inline mr-1" />
            วันที่มีผล <span className="text-danger-500">*</span>
          </label>
          <input
            type="date"
            aria-label="วันที่มีผล"
            name="effectiveDate"
            value={effectiveDate}
            onChange={(e) => {
              setEffectiveDate(e.target.value);
              // Clear error when user changes value
              if (errors.effectiveDate) {
                setErrors(prev => ({ ...prev, effectiveDate: '' }));
              }
            }}
            className={cn('input', errors.effectiveDate && 'border-danger-300')}
            aria-invalid={Boolean(errors.effectiveDate)}
            aria-describedby={errors.effectiveDate ? 'effectiveDate-error' : undefined}
          />
          {errors.effectiveDate && (
            <p id="effectiveDate-error" className="mt-1 text-sm text-danger-600">{errors.effectiveDate}</p>
          )}
        </div>

        {/* เหตุผล */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            <RiFileTextLine className="w-4 h-4 inline mr-1" />
            เหตุผลในการเปลี่ยนสถานะ <span className="text-danger-500">*</span>
          </label>
          <textarea
            name="reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              // Clear error when user changes value
              if (errors.reason) {
                setErrors(prev => ({ ...prev, reason: '' }));
              }
            }}
            rows={3}
            placeholder="ระบุเหตุผลในการเปลี่ยนสถานะรายการเช่าทั้งหมดนี้..."
            className={cn('input', errors.reason && 'border-danger-300')}
            maxLength={500}
            aria-invalid={Boolean(errors.reason)}
            aria-describedby={errors.reason ? 'reason-error' : undefined}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.reason ? (
              <p id="reason-error" className="text-sm text-danger-600">{errors.reason}</p>
            ) : (
              <p className="text-xs text-secondary-500">
                อธิบายเหตุผลอย่างละเอียด (อย่างน้อย 10 ตัวอักษร)
              </p>
            )}
            <span className="text-xs text-secondary-500">
              {reason.length}/500
            </span>
          </div>
        </div>

        {/* คำเตือน */}
        {newStatus && newStatus === RentalStatus.CANCELLED && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex">
              <RiAlertLine className="w-5 h-5 text-warning-400 mr-2 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-warning-800">คำเตือน</h4>
                <p className="text-sm text-warning-700 mt-1">
                  การเปลี่ยนเป็นสถานะ "{getStatusLabel(newStatus)}" จะส่งผลต่อการทำงานของระบบ
                  กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkStatusUpdateModal;